import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import api from '../services/api';
import ProfileHeader from '../components/ui/ProfileHeader';
import EventCard from '../components/ui/EventCard';
import { EventGridSkeleton } from '../components/ui/EventCardSkeleton';
import EmptyState from '../components/ui/EmptyState';
import { motion } from 'framer-motion';

const TABS = [
  { key: 'events', label: 'Events' },
  { key: 'hosted', label: 'Hosted' },
  { key: 'attending', label: 'Attending' },
];

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.06 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: [0.25, 0.1, 0.25, 1] },
  },
};

function UserProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);

  const [profile, setProfile] = useState(null);
  const [publicEvents, setPublicEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [activeTab, setActiveTab] = useState('events');
  const [error, setError] = useState(null);

  const fetchUserProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get(`/api/users/${id}`);
      // Unwrap the standard { success, message, data } response envelope.
      const userData = (response.data && response.data.data) ? response.data.data : response.data;
      setProfile(userData);

      // Extract public, active events from hosted events
      const hostedEvents =
        userData.HostedEvents || userData.hosted_events || [];
      const now = new Date();
      const publicActiveEvents = hostedEvents.filter((event) => {
        const startTime = new Date(event.StartTime || event.start_time);
        const visibility =
          event.Visibility || event.visibility || 'public';
        return startTime > now && visibility === 'public';
      });
      setPublicEvents(publicActiveEvents);

      // Check if current user is following this user
      if (currentUser && userData.Followers) {
        setIsFollowing(
          userData.Followers.some(
            (follower) =>
              (follower.ID || follower.id) ===
              (currentUser.id || currentUser.ID)
          )
        );
      }
    } catch (err) {
      console.error('Failed to load user profile:', err);
      setError(
        `Failed to load profile: ${
          err.message || 'User may not exist or server is unavailable'
        }`
      );
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [id, currentUser]);

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  const handleFollow = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    // Optimistic update
    const wasFollowing = isFollowing;
    setIsFollowing(!wasFollowing);
    setProfile((prev) => ({
      ...prev,
      followers_count:
        (prev.followers_count || 0) + (wasFollowing ? -1 : 1),
    }));

    try {
      if (wasFollowing) {
        await api.delete(`/api/users/${id}/follow`);
      } else {
        await api.post(`/api/users/${id}/follow`);
      }
    } catch (err) {
      console.error('Failed to update follow status:', err);
      // Revert optimistic update
      setIsFollowing(wasFollowing);
      setProfile((prev) => ({
        ...prev,
        followers_count:
          (prev.followers_count || 0) + (wasFollowing ? 1 : -1),
      }));
    }
  };

  const handleMessage = () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    // There is no /messages/:id route; /messages is the conversation list.
    // Pass the target user as a query param so the page can preselect them.
    navigate(`/messages?user=${id}`);
  };

  // --- Determine ownership ---
  const isOwner =
    currentUser &&
    (currentUser.id || currentUser.ID) === (profile?.ID || profile?.id);

  // --- Get events for each tab ---
  const getHostedEvents = () =>
    profile?.HostedEvents || profile?.hosted_events || [];

  const getAttendingEvents = () =>
    profile?.AttendingEvents || profile?.attending_events || [];

  const getTabEvents = () => {
    switch (activeTab) {
      case 'hosted':
        return getHostedEvents();
      case 'attending':
        return getAttendingEvents();
      case 'events':
      default:
        return publicEvents;
    }
  };

  const getEmptyMessage = () => {
    const name = profile?.first_name || profile?.username || 'This user';
    switch (activeTab) {
      case 'hosted':
        return {
          title: 'No hosted events',
          description: `${name} has not hosted any events yet.`,
        };
      case 'attending':
        return {
          title: 'Not attending any events',
          description: `${name} is not currently attending any events.`,
        };
      case 'events':
      default:
        return {
          title: 'No public events',
          description: `${name} does not have any active public events right now.`,
        };
    }
  };

  // --- Loading state ---
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Profile skeleton */}
        <div className="bg-white rounded-2xl shadow-soft-sm border border-gray-100 overflow-hidden">
          <div className="h-32 sm:h-40 skeleton" />
          <div className="px-4 sm:px-6 pb-5">
            <div className="flex items-end justify-between -mt-12 mb-4">
              <div className="w-24 h-24 rounded-2xl skeleton border-4 border-white" />
              <div className="w-24 h-9 skeleton rounded-xl" />
            </div>
            <div className="h-6 w-48 skeleton rounded-md mb-2" />
            <div className="h-4 w-28 skeleton rounded-md mb-4" />
            <div className="flex gap-6 pt-3 border-t border-surface-100">
              <div className="h-10 w-16 skeleton rounded-md" />
              <div className="h-10 w-16 skeleton rounded-md" />
              <div className="h-10 w-16 skeleton rounded-md" />
            </div>
          </div>
        </div>

        {/* Events skeleton */}
        <EventGridSkeleton count={6} />
      </div>
    );
  }

  // --- Error state ---
  if (error) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <div className="bg-white rounded-2xl shadow-soft-sm border border-gray-100 p-8">
          <div className="w-14 h-14 rounded-2xl bg-error-50 text-error-500 flex items-center justify-center mx-auto mb-5">
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-h2 text-surface-900 mb-2">Profile Error</h1>
          <p className="text-body text-surface-500 mb-6">{error}</p>
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={fetchUserProfile}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-body-sm font-semibold text-surface-700 bg-white border border-surface-200 rounded-xl hover:bg-surface-50 focus:outline-none focus:ring-2 focus:ring-primary-500/30 transition-colors duration-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Retry
            </button>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-body-sm font-semibold rounded-xl hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500/40 transition-colors duration-200"
            >
              Go Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // --- Not found state ---
  if (!profile) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <div className="bg-white rounded-2xl shadow-soft-sm border border-gray-100 p-8">
          <div className="w-14 h-14 rounded-2xl bg-surface-100 text-surface-400 flex items-center justify-center mx-auto mb-5">
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h1 className="text-h2 text-surface-900 mb-2">User not found</h1>
          <p className="text-body text-surface-500 mb-6">
            The profile you are looking for does not exist.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-body-sm font-semibold rounded-xl hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500/40 transition-colors duration-200"
          >
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  // --- Build profile data for ProfileHeader ---
  const isBusinessAccount =
    profile.account_type === 'business' ||
    profile.account_type === 'organization';

  const profileHeaderData = {
    username: profile.username || profile.Username || '',
    display_name: isBusinessAccount
      ? profile.business_name || profile.BusinessName || ''
      : [profile.first_name, profile.last_name].filter(Boolean).join(' ') ||
        profile.username ||
        '',
    bio: profile.bio || profile.description || '',
    avatar: profile.avatar || profile.Avatar || '',
    events_count: profile.events_created_count || getHostedEvents().length || 0,
    followers_count: profile.followers_count || 0,
    attending_count:
      profile.events_joined_count || getAttendingEvents().length || 0,
  };

  const tabEvents = getTabEvents();
  const emptyMsg = getEmptyMessage();

  // Choose which tabs to display
  const visibleTabs = isOwner
    ? TABS
    : TABS;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      {/* Profile Header */}
      <ProfileHeader
        profile={profileHeaderData}
        isOwner={isOwner}
        isFollowing={isFollowing}
        onFollow={handleFollow}
        onMessage={!isOwner ? handleMessage : undefined}
      />

      {/* Business Information */}
      {isBusinessAccount && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.35 }}
          className="bg-white rounded-2xl shadow-soft-sm border border-gray-100 p-5 sm:p-6"
        >
          <h2 className="text-h3 text-surface-900 mb-4">Business Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {profile.address && (
              <div>
                <span className="text-caption text-surface-400 block mb-1">Address</span>
                <span className="text-body text-surface-700">{profile.address}</span>
              </div>
            )}
            {profile.business_phone && (
              <div>
                <span className="text-caption text-surface-400 block mb-1">Phone</span>
                <span className="text-body text-surface-700">{profile.business_phone}</span>
              </div>
            )}
            {profile.operating_hours && (
              <div>
                <span className="text-caption text-surface-400 block mb-1">Hours</span>
                <span className="text-body text-surface-700">{profile.operating_hours}</span>
              </div>
            )}
            {profile.business_type && (
              <div>
                <span className="text-caption text-surface-400 block mb-1">Type</span>
                <span className="text-body text-surface-700">{profile.business_type}</span>
              </div>
            )}
          </div>

          {/* Tags */}
          {profile.tags && profile.tags.length > 0 && (
            <div className="mt-4 pt-4 border-t border-surface-100">
              <span className="text-caption text-surface-400 block mb-2">Tags</span>
              <div className="flex flex-wrap gap-2">
                {profile.tags.map((tag) => (
                  <span
                    key={tag.ID || tag.id}
                    className="px-3 py-1 bg-primary-50 text-primary-700 rounded-lg text-body-sm font-medium"
                  >
                    {tag.Name || tag.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Social Links */}
      {(() => {
        let socialLinks = {};
        try {
          socialLinks = profile.social_links
            ? typeof profile.social_links === 'string'
              ? JSON.parse(profile.social_links)
              : profile.social_links
            : {};
        } catch {
          socialLinks = {};
        }
        return Object.keys(socialLinks).length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {Object.entries(socialLinks).map(([platform, url]) => (
              <a
                key={platform}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-body-sm font-medium text-surface-600 bg-white border border-surface-200 rounded-xl hover:bg-surface-50 hover:border-primary-300 hover:text-primary-600 transition-colors duration-200"
              >
                {platform}
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            ))}
          </div>
        ) : null;
      })()}

      {/* Tab Navigation */}
      <div className="border-b border-surface-200">
        <nav className="flex gap-1" aria-label="Profile tabs">
          {visibleTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`relative px-4 py-3 text-body-sm font-semibold transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30 rounded-t-lg ${
                activeTab === tab.key
                  ? 'text-primary-600'
                  : 'text-surface-400 hover:text-surface-600'
              }`}
              aria-selected={activeTab === tab.key}
              role="tab"
            >
              {tab.label}
              {activeTab === tab.key && (
                <motion.div
                  layoutId="profileTabUnderline"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500 rounded-full"
                  transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                />
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[300px]">
        {tabEvents.length === 0 ? (
          <EmptyState
            icon={
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            }
            title={emptyMsg.title}
            description={emptyMsg.description}
          />
        ) : (
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            key={activeTab}
          >
            {tabEvents.map((event) => {
              const eventId = event.ID || event.id;
              return (
                <motion.div key={eventId} variants={cardVariants}>
                  <EventCard event={event} />
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default UserProfile;
